// LandingGuide — first-run wayfinding over the map (US-877 · R-1/R-2).
//
// On the APAC home view, before any selection, a restrained card tells the
// analyst how to start: pick a DMC from the header selector, or click a country
// to drill in. It dismisses on the first selection (map or dropdown) and on an
// explicit close, and does not return until the view is reset home (Clear /
// drill-up to APAC). Reads interaction state from UIContext; owns no map or data.
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import useUI from "../../hooks/useUI";

const Hint = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box sx={{ display: "flex", color: "primary.main", flexShrink: 0 }}>{icon}</Box>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  </Box>
);

const LandingGuide = () => {
  const { hasInteracted, selectedCountry, markInteracted } = useUI();
  const { tokens } = useTheme();
  // A pure crossfade is reduced-motion safe; collapse it to instant when the user
  // asks for less motion (impeccable: every animation needs a reduced-motion path).
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const show = !hasInteracted && !selectedCountry;

  return (
    <Fade in={show} appear timeout={reduceMotion ? 0 : 320} unmountOnExit>
      <Box
        role="note"
        aria-label="Getting started"
        sx={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 6,
          width: "min(440px, calc(100% - 32px))",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          boxShadow: tokens.shadows.card,
          p: 2,
        }}
      >
        <IconButton
          size="small"
          onClick={markInteracted}
          aria-label="Dismiss getting started"
          sx={{ position: "absolute", top: 4, right: 4, color: "text.disabled" }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Typography
          component="h2"
          variant="h6"
          sx={{ color: "primary.main", pr: 3, mb: 0.5, lineHeight: 1.2 }}
        >
          Explore connectivity across Asia-Pacific
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Choose a Development Member Country to focus the map, then drill into its
          provinces and districts.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Hint icon={<PublicOutlinedIcon sx={{ fontSize: 18 }} />}>
            Pick a country from the selector in the header
          </Hint>
          <Hint icon={<TouchAppOutlinedIcon sx={{ fontSize: 18 }} />}>
            Or click any country on the map to drill in
          </Hint>
        </Box>
      </Box>
    </Fade>
  );
};

export default LandingGuide;
